import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Chip, Stack, Typography,
  IconButton, InputAdornment, FormControlLabel, Checkbox,
  CircularProgress, Alert, Box, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

export default function ZephyrComposeModal({
  open,
  onClose,
  defaultTo = "",
  invoiceID = null,
  claimID = null,
  customerName = "",
  claimNumber = "",
  chemistEmail = "",
  insurerEmail = "",
  onSuccess,
  onError,
}) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selectedInvoiceIDs, setSelectedInvoiceIDs] = useState(new Set());
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [invoiceView, setInvoiceView] = useState(null); // { invoiceID, scripts, loading }

  useEffect(() => {
    if (!open) return;
    setTo(insurerEmail || defaultTo || "");
    setSubject(customerName && claimNumber ? `${customerName} - ${claimNumber}` : "");
    setBody("");
    setConfirming(false);
    setAvailableInvoices([]);
    setSelectedInvoiceIDs(new Set());

    if (!claimID) return;
    setLoadingInvoices(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_CLAIM_INVOICES}/${claimID}`, "GET")
      .then(res => {
        const invoices = res?.data ?? [];
        setAvailableInvoices(invoices);
        setSelectedInvoiceIDs(
          invoiceID
            ? new Set([invoiceID])
            : new Set(invoices.map(i => i.invoiceID))
        );
      })
      .catch(() => {})
      .finally(() => setLoadingInvoices(false));
  }, [open, defaultTo, invoiceID, claimID, customerName, claimNumber, insurerEmail]);

  const toggleInvoice = (id) => {
    setSelectedInvoiceIDs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openInvoiceView = (id) => {
    setInvoiceView({ invoiceID: id, scripts: [], loading: true });
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${id}`, "GET")
      .then(res => setInvoiceView({ invoiceID: id, scripts: res?.data?.scripts ?? [], loading: false }))
      .catch(() => setInvoiceView({ invoiceID: id, scripts: [], loading: false }));
  };

  const bodyMentionsAttachments = /attach|enclosed|please find/i.test(body);
  const attachmentWarning = bodyMentionsAttachments && selectedInvoiceIDs.size === 0;

  const handleSend = async () => {
    setLoading(true);
    try {
      await phappsRequest(API_PATHS.ZEPHYR_ADMIN_COMPOSE_EMAIL, "POST", {
        to: to.trim(),
        subject: subject.trim(),
        body: body.trim(),
        invoiceIDs: [...selectedInvoiceIDs],
        claimID: claimID ?? undefined,
      });
      onSuccess?.("Email sent");
      onClose();
    } catch (err) {
      onError?.(err?.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  const canSend = to.trim() && subject.trim() && !loading;

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Compose Email</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>

          {/* To field + quick-fill chips */}
          <Box>
            <TextField
              label="To"
              value={to}
              onChange={e => setTo(e.target.value)}
              size="small"
              fullWidth
              type="email"
              disabled={loading}
            />
            {(insurerEmail || chemistEmail) && (
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: "wrap" }}>
                {insurerEmail && (
                  <Chip
                    label={`Insurer: ${insurerEmail}`}
                    size="small"
                    onClick={() => setTo(insurerEmail)}
                    clickable
                    variant="outlined"
                    color="primary"
                  />
                )}
                {chemistEmail && (
                  <Chip
                    label={`Chemist: ${chemistEmail}`}
                    size="small"
                    onClick={() => setTo(chemistEmail)}
                    clickable
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Stack>
            )}
          </Box>

          {/* Subject with clear button */}
          <TextField
            label="Subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            size="small"
            fullWidth
            disabled={loading}
            InputProps={{
              endAdornment: subject ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSubject("")} edge="end" tabIndex={-1}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Body — signature auto-appended by backend */}
          <TextField
            label="Body"
            value={body}
            onChange={e => setBody(e.target.value)}
            multiline
            rows={7}
            fullWidth
            disabled={loading}
            inputProps={{ style: { fontFamily: "monospace", fontSize: "0.85rem" } }}
          />

          {/* Invoice list */}
          {claimID && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Attach Invoices</Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" onClick={() => setSelectedInvoiceIDs(new Set(availableInvoices.map(i => i.invoiceID)))} disabled={loading}>
                    Select All
                  </Button>
                  <Button size="small" onClick={() => setSelectedInvoiceIDs(new Set())} disabled={loading}>
                    Select None
                  </Button>
                </Stack>
              </Stack>
              {loadingInvoices ? (
                <CircularProgress size={18} />
              ) : availableInvoices.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No unpaid invoices</Typography>
              ) : (
                <Box sx={{ maxHeight: 160, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, px: 1 }}>
                  {availableInvoices.map(inv => (
                    <Box key={inv.invoiceID} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedInvoiceIDs.has(inv.invoiceID)}
                            onChange={() => toggleInvoice(inv.invoiceID)}
                            size="small"
                            disabled={loading}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Invoice #{inv.invoiceID} — ${Number(inv.total).toFixed(2)}
                          </Typography>
                        }
                        sx={{ flex: 1, m: 0 }}
                      />
                      <IconButton size="small" onClick={() => openInvoiceView(inv.invoiceID)} sx={{ p: 0.25 }}>
                        <OpenInNewIcon sx={{ fontSize: 14, color: "#64748b" }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={() => setConfirming(true)}
          variant="contained"
          disabled={!canSend}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Send
        </Button>
      </DialogActions>

      {/* Invoice detail view */}
      <Dialog open={!!invoiceView} onClose={() => setInvoiceView(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>
          Invoice #{invoiceView?.invoiceID} — Scripts
        </DialogTitle>
        <DialogContent>
          {invoiceView?.loading ? (
            <CircularProgress size={20} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Script ID", "Item", "Qty", "Amount", "Status"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", py: 0.75 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(invoiceView?.scripts ?? []).map(s => (
                  <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.5, fontSize: "0.8rem" } }}>
                    <TableCell>{s.ScriptID}</TableCell>
                    <TableCell>{s.txtItem}</TableCell>
                    <TableCell align="center">{s.Qty ?? "—"}</TableCell>
                    <TableCell align="right">${Number(s.InvAmount ?? 0).toFixed(2)}</TableCell>
                    <TableCell>
                      {s.PaymentID ? "Paid" : Number(s.WrittenOff) ? "Written Off" : "Outstanding"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceView(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>Confirm Send</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              Sending email to <strong>{to.trim() || "—"}</strong> — are you sure?
            </Typography>
            {attachmentWarning && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                Your message mentions attachments but no invoices are selected.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            onClick={() => { setConfirming(false); handleSend(); }}
            variant="contained"
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
